import { z } from 'zod';
import { requireFields } from '@utils/form-schema';
import type { IHistoryDiagnosedModel } from '@/types/history/history-diagnosed';

type TFlags = Pick<
    IHistoryDiagnosedModel,
    | 'useReducedQuestions'
    | 'isLiverRegistration'
    | 'isFemale'
    | 'isMale'
    | 'requirePregnancyFields'
    | 'yesValue'
    | 'medicalStatusStillBeingTreatedValue'
    | 'medicalStatusTreatedInPastValue'
>;

export const createHistoryDiagnosedSchema = (model: TFlags) => {
    const yes = String(model.yesValue);
    const still = String(model.medicalStatusStillBeingTreatedValue);
    const treatedPast = String(model.medicalStatusTreatedInPastValue);

    const alwaysRequired = [
        'aboType',
        'hasHistoryOfHighBp',
        'hasHadCabg',
        'hasHistoryOfBloodClots',
        'hasHistoryOfHighCholOrTg',
        'hasStents',
        'historyChestPainExercise',
        'historyChestPainOther',
        'resultsOfClimbingStairs',
        'hasHadStroke',
        'hasHadLegBypass',
        'historyLungDisease',
        'hasGastroIssues',
        'historyPancreatitis',
        'hasBleedingProblem',
        'historyKidneyDamage',
        'historyNeuroDisease',
        'historySeizure',
        'hasLupus',
        'historyParalysis',
        'hasArthritis',
        'historyNeuropathy',
        'historyDepression',
        'historyAnxiety',
        'hasHistoryOfPsychiatric',
        'hasReceivedTransfusion',
        'hasFabrys',
        'hasSickleCell',
        'hasSickleCellTrait',
        'hasSarcoidosis',
        'hasAutoImmune',
        'hasHadPhysicalExam',
        'hasAnimalBiteExposure',
    ] as const;

    return z.record(z.string(), z.string())
        .superRefine((data, ctx) => {
            const v = (field: string) => (data as Record<string, string>)[field] ?? '';

            requireFields(ctx, data, ...alwaysRequired);

            const reduced = model.useReducedQuestions;
            const liver = model.isLiverRegistration;

            if (v('hasHistoryOfHighBp') === still) {
                requireFields(ctx, data, 'highBpAgents');
            }

            if (model.requirePregnancyFields) {
                requireFields(ctx, data, 'hadHighBpDuringPregnancy');
            }

            if (!reduced) {
                requireFields(ctx, data, 'hasHistoryOfCoronaryDisease', 'hadStressTestWithinYear');
            }

            if (v('hadStressTestWithinYear') === yes) {
                requireFields(ctx, data, 'stressTestLocation', 'stressTestResults');
            }

            if (v('hasHadStroke') === yes) {
                requireFields(ctx, data, 'howManyStrokes');
            }

            if (v('hasHadLegBypass') === yes) {
                requireFields(ctx, data, 'howManyLegBypasses');
            }

            if (!reduced) {
                requireFields(ctx, data, 'hasCopd', 'hasTb', 'historyPneumonia', 'hasRespiratoryIssues');
            }

            if (v('hasRespiratoryIssues') === yes) {
                requireFields(ctx, data, 'respiratoryIssuesDescription');
            }

            if (v('hasGastroIssues') === yes) {
                requireFields(ctx, data, 'gastroIssuesDescription');
            }

            if (!reduced) {
                requireFields(ctx, data, 'historyGallstones');
            }

            if (!liver) {
                requireFields(ctx, data, 'historyLiverDisease');
            }

            if (!liver && !reduced) {
                requireFields(ctx, data, 'hasHepB', 'hasHepC');
            }

            if (!reduced) {
                requireFields(ctx, data, 'hasHadUti');
            }

            if (v('hasHadUti') === yes) {
                requireFields(ctx, data, 'utiEpisodes', 'utiRemissionStatus');
            }

            if (!reduced) {
                requireFields(ctx, data, 'historyProteinuria', 'historyHematuria');
            }

            const psychiatric = v('hasHistoryOfPsychiatric');

            if (psychiatric === still || psychiatric === treatedPast) {
                requireFields(ctx, data, 'psychiatricDescription');
            }

            const femaleFull = model.isFemale && !reduced;

            if (femaleFull) {
                requireFields(ctx, data, 'historyFibroidUterus', 'historyEndometriosis', 'historyPolycysticOvaries');
            }

            if (model.isFemale) {
                requireFields(ctx, data, 'hasHadMiscarriages');
            }

            if (v('hasHadMiscarriages') === yes) {
                requireFields(ctx, data, 'howManyMiscarriages', 'hadLateMiscarriage');
            }

            if (femaleFull) {
                requireFields(ctx, data, 'hasHadAbortions');
            }

            if (v('hasHadAbortions') === yes) {
                requireFields(ctx, data, 'howManyAbortions');
            }

            if (v('hasReceivedTransfusion') === yes) {
                requireFields(ctx, data, 'howManyTransfusions');
            }

            if (!reduced) {
                requireFields(ctx, data, 'hasHistoryOfObesity');
            }

            if (v('hasAutoImmune') === yes) {
                requireFields(ctx, data, 'autoImmuneDescription');
            }

            if (v('hasHadPhysicalExam') === yes) {
                requireFields(ctx, data, 'physicalExamYearsAgo');
            }

            const femaleScreenings = !reduced && !model.isMale;

            if (femaleScreenings) {
                requireFields(ctx, data, 'hasHadMammogram', 'hasHadPapSmear');
            }

            if (v('hasHadMammogram') === yes) {
                requireFields(ctx, data, 'mammogramYearsAgo');
            }

            if (v('hasHadPapSmear') === yes) {
                requireFields(ctx, data, 'papSmearYearsAgo');
            }

            if (model.isMale) {
                requireFields(ctx, data, 'hasHadPsaTest');
            }

            if (v('hasHadPsaTest') === yes) {
                requireFields(ctx, data, 'psaTestYearsAgo');
            }

            if (!reduced) {
                requireFields(ctx, data, 'hasHadColonoscopy');
            }

            if (v('hasHadColonoscopy') === yes) {
                requireFields(ctx, data, 'colonoscopyYearsAgo');
            }

            if (liver) {
                requireFields(ctx, data, 'hasActiveInfection');
            }

            if (v('hasActiveInfection') === yes) {
                requireFields(ctx, data, 'activeInfectionDescription');
            }

            if (v('hasAnimalBiteExposure') === yes) {
                requireFields(ctx, data, 'animalBiteDescription');
            }
        });
};
